import React, { useState } from "react"

import { Loader2 } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MIN_PASSWORD_LENGTH, useAuth } from "@/contexts/AuthContext"

/** Partner logins only. No mail server, so this is the only self-serve password change. */
const ChangePasswordDialog: React.FC<{
  open: boolean
  onOpenChange: (open: boolean) => void
}> = ({ open, onOpenChange }) => {
  const { changePassword } = useAuth()
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [saving, setSaving] = useState(false)

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== password
  const ready = password.length >= MIN_PASSWORD_LENGTH && confirm === password

  const close = () => {
    setPassword("")
    setConfirm("")
    onOpenChange(false)
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!ready) return
    setSaving(true)
    try {
      await changePassword(password)
      toast.success("Password changed")
      close()
    } catch (error) {
      toast.error("Password not changed", {
        description: error instanceof Error ? error.message : "Please try again.",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent size="confirm">
        <form onSubmit={(e) => void onSubmit(e)}>
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              Choose a password of at least {MIN_PASSWORD_LENGTH} characters. You stay signed in
              here.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="grid gap-3">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={tooShort}
                  className="mt-1"
                />
                {tooShort && (
                  <p className="mt-1 text-xs text-red-600">
                    At least {MIN_PASSWORD_LENGTH} characters.
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="confirm-password">Type it again</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  aria-invalid={mismatch}
                  className="mt-1"
                />
                {mismatch && <p className="mt-1 text-xs text-red-600">The two don&rsquo;t match.</p>}
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready || saving} className="gap-1.5">
              {saving && <Loader2 size={14} className="animate-spin" />}
              Change password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default ChangePasswordDialog
